'use client'

import { useMemo } from 'react'
import { useTranslations } from 'next-intl'

import { AppVariant } from '@common/enums'
import { getMenuItemsWithActiveState } from '@common/utils/getMenuItemsWithActiveState'

import { selectGateways, useGatewaysStore } from '@modules/gateways'
import {
  applyMobileSettingsNavDisabledState,
  applyWorkspaceNavDisabledState,
  filterDisabledSettingsNavItems,
  SETTINGS_NAV_ITEMS,
} from '@modules/settings'
import type { Workspace } from '@modules/workspaces'

import { MENU_ITEMS } from '../layout.const'

import { useVisionGatewayMenuItems } from './useVisionGatewayMenuItems'

type UseLayoutMenuItemsParams = {
  selectedNavId: AppVariant
  isSettingsRoute: boolean
  isVisionRoute: boolean
  pathname: string
  locale: string
  currentWorkspace: Workspace | null
  workspaceDataPending?: boolean
}

export function useLayoutMenuItems({
  selectedNavId,
  isSettingsRoute,
  isVisionRoute,
  pathname,
  locale,
  currentWorkspace,
  workspaceDataPending = false,
}: UseLayoutMenuItemsParams) {
  const t = useTranslations('core')
  const gateways = useGatewaysStore(selectGateways)

  const menuItems = useMemo(
    () => getMenuItemsWithActiveState(MENU_ITEMS[selectedNavId], pathname, locale),
    [selectedNavId, pathname, locale]
  )

  const visionGatewayMenuItems = useVisionGatewayMenuItems({
    gateways,
    isVisionRoute,
    noGatewaysLabel: t('sidebar.noGateways'),
    selectedNavId,
  })

  const workspaceNavEnabled = !!currentWorkspace || workspaceDataPending

  const settingsNavForWorkspace = useMemo(
    () => applyWorkspaceNavDisabledState(SETTINGS_NAV_ITEMS, workspaceNavEnabled),
    [workspaceNavEnabled]
  )

  const settingsMenuItems = useMemo(
    () => getMenuItemsWithActiveState(filterDisabledSettingsNavItems(settingsNavForWorkspace), pathname, locale),
    [settingsNavForWorkspace, pathname, locale]
  )

  const settingsMobileMenuItems = useMemo(
    () =>
      getMenuItemsWithActiveState(
        filterDisabledSettingsNavItems(applyMobileSettingsNavDisabledState(settingsNavForWorkspace)),
        pathname,
        locale
      ),
    [settingsNavForWorkspace, pathname, locale]
  )

  const mainNav = visionGatewayMenuItems ?? menuItems ?? []

  const sidebarMenuItems = (isSettingsRoute ? settingsMenuItems : mainNav) ?? []
  const mobileMenuItems = (isSettingsRoute ? settingsMobileMenuItems : mainNav) ?? []

  return { sidebarMenuItems, mobileMenuItems }
}
